import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import TournamentAdminLayout from './components/TournamentAdminLayout';
import useTournamentFeatures from './hooks/useTournamentFeatures';
// import FeatureLocked from './components/FeatureLocked';
import { copyToClipboard } from './utils/clipboard';
import { API_BASE_URL } from './utils/apiConfig';
import './styles-tournament-overview.css';

function TournamentOverview() {
  const { code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [auctionData, setAuctionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [modalState, setModalState] = useState({ open: false, scope: 'player' });
  const isSuperAdmin = currentUser?.role === 'SuperAdmin' || currentUser?.role === 'SUPER_ADMIN';
  const {
    loading: featuresLoading,
    error: featureError,
    planLabel,
    hasFeature,
    refetch: refetchFeatures
  } = useTournamentFeatures(code);

  const baseUrl = window.location.origin;

  const fetchData = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [tournamentRes, playersRes, teamsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/tournaments/${code}`, { headers }),
        axios
          .get(`${API_BASE_URL}/api/players/${code}`, { headers })
          .catch(() => ({ data: { players: [] } })),
        axios
          .get(`${API_BASE_URL}/api/teams/${code}`, { headers })
          .catch(() => ({ data: { teams: [] } }))
      ]);

      setTournament(tournamentRes.data.tournament);
      setPlayers(playersRes.data.players || []);
      setTeams(teamsRes.data.teams || []);

      try {
        const auctionRes = await axios.get(`${API_BASE_URL}/api/auctions/summary/${code}`, { headers });
        setAuctionData(auctionRes.data.summary);
      } catch {
        setAuctionData(null);
      }

      refetchFeatures();
    } catch (err) {
      console.error('Error fetching dashboard data', err);
      if (!showRefreshing) {
        toast.error('Failed to load tournament dashboard');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [code, refetchFeatures]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [code, fetchData]);

  useEffect(() => {
    if (featureError) {
      toast.warning(featureError);
    }
  }, [featureError]);

  const mutateRegistration = async (action, scope) => {
    if (featuresLoading) {
      toast.info('Checking feature permissions…');
      return;
    }
    if (!hasFeature('registration_portal')) {
      toast.info('🔒 Registration portal is locked for this plan.');
      setModalState({ open: false, scope: 'player' });
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/tournaments/${code}/${action}-registration`,
        { scope },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        action === 'close'
          ? `Closed ${scope === 'both' ? 'player & team' : scope} registration`
          : `Reopened ${scope === 'both' ? 'player & team' : scope} registration`
      );
      fetchData();
    } catch (err) {
      console.error(`Error trying to ${action} registration`, err);
      toast.error(`Could not ${action} ${scope} registration`);
    } finally {
      setModalState({ open: false, scope: 'player' });
    }
  };

  const copyLink = async (scope) => {
    if (featuresLoading) {
      toast.info('Checking feature permissions…');
      return;
    }
    if (!hasFeature('registration_portal')) {
      toast.info('🔒 Registration portal is locked for this plan.');
      return;
    }
    const path = scope === 'team' ? `/register/team/${code}` : `/register/${code}`;
    const link = `${baseUrl}${path}`;
    const success = await copyToClipboard(link);
    if (success) {
      toast.success('✅ Link copied');
    } else {
      toast.error('Failed to copy link');
    }
  };

  const shareOnWhatsApp = (scope) => {
    if (featuresLoading) {
      toast.info('Checking feature permissions…');
      return;
    }
    if (!hasFeature('registration_portal')) {
      toast.info('🔒 Registration portal is locked for this plan.');
      return;
    }
    const path = scope === 'team' ? `/register/team/${code}` : `/register/${code}`;
    const link = `${baseUrl}${path}`;
    const message = `🏆 Register for ${tournament?.name || 'this tournament'}!\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const startAuction = useCallback(async () => {
    if (featuresLoading) {
      toast.info('Checking feature permissions…');
      return;
    }
    if (!hasFeature('auction_live')) {
      toast.info('🔒 Live auction is not available in your current plan.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/tournaments/${code}/start-auction`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Auction started successfully');
      navigate(`/tournament/${code}/auction`);
    } catch (err) {
      console.error('Error starting auction', err);
      const errorData = err.response?.data;
      if (errorData?.code === 'AUCTION_NOT_READY') {
        toast.warning('Registration/quorum incomplete. You can start with bypass on the auction page.');
        navigate(`/tournament/${code}/auction`);
      } else {
        toast.error(errorData?.message || 'Unable to start auction');
      }
    }
  }, [featuresLoading, hasFeature, code, navigate]);

  const handleBackNavigation = () => {
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }
    navigate('/dashboard/superadmin');
  };

  const computeRegistrationStatus = (scope) => {
    if (!tournament) return 'Not Started';
    const now = new Date();
    const start = tournament.registrationStartDate ? new Date(tournament.registrationStartDate) : null;
    const end = tournament.registrationEndDate ? new Date(tournament.registrationEndDate) : null;

    const isEnabled = scope === 'player'
      ? Boolean(tournament.playerRegistrationEnabled)
      : Boolean(tournament.teamRegistrationEnabled);

    if (isEnabled) {
      return 'Active';
    }

    if (start && now < start) return 'Not Started';
    if (end && now > end) return 'Closed';

    return 'Closed';
  };

  const formatDate = (value, fallback = 'TBD') => {
    if (!value) return fallback;
    try {
      return new Date(value).toLocaleDateString('en-IN', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return fallback;
    }
  };

  const formatTime = (value, fallback = '') => {
    if (!value) return fallback;
    try {
      return new Date(value).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return fallback;
    }
  };

  const safeTournament = useMemo(() => tournament || {}, [tournament]);
  const registeredPlayers = players.length;
  const registeredTeams = teams.length;
  const totalPlayerPoolSize = safeTournament.playerPoolSize || safeTournament.maxPlayers || 100;
  const maxTeams = safeTournament.participatingTeams || 8;
  const playerStatus = computeRegistrationStatus('player');
  const teamStatus = computeRegistrationStatus('team');
  const soldPlayerEntries = useMemo(
    () => players.filter((p) => p.auctionStatus === 'Sold'),
    [players]
  );
  const soldPlayers = soldPlayerEntries.length;
  const totalSpend = soldPlayerEntries.reduce((sum, p) => sum + (p.soldPrice || 0), 0);
  const highestBid = auctionData?.highestBid ? Number(auctionData.highestBid) : null;
  const canManagePlayers = !featuresLoading && hasFeature('registration_portal');
  const canStartAuction = !featuresLoading && hasFeature('auction_live');

  const heroStats = useMemo(
    () => [
      {
        label: 'Players',
        value: `${registeredPlayers}/${totalPlayerPoolSize}`,
        hint: 'Registered'
      },
      {
        label: 'Teams',
        value: `${registeredTeams}/${maxTeams}`,
        hint: 'Confirmed'
      },
      {
        label: 'Auction',
        value: `₹${totalSpend.toLocaleString('en-IN')}`,
        hint: `${soldPlayers} sold`,
        hideOnMobile: true
      },
      {
        label: 'Status',
        value: tournament?.status || 'Upcoming',
        hint: planLabel,
        hideOnMobile: true
      }
    ],
    [registeredPlayers, totalPlayerPoolSize, registeredTeams, maxTeams, totalSpend, soldPlayers, tournament?.status, planLabel]
  );

  const timelineItems = useMemo(() => {
    const items = [];
    if (safeTournament.registrationStartDate) {
      items.push({
        title: 'Registration opens',
        date: formatDate(safeTournament.registrationStartDate),
        time: formatTime(safeTournament.registrationStartDate),
        status: new Date() < new Date(safeTournament.registrationStartDate) ? 'upcoming' : 'completed'
      });
    }
    if (safeTournament.registrationEndDate) {
      items.push({
        title: 'Registration closes',
        date: formatDate(safeTournament.registrationEndDate),
        time: formatTime(safeTournament.registrationEndDate),
        status: new Date() > new Date(safeTournament.registrationEndDate) ? 'completed' : 'upcoming'
      });
    }
    if (safeTournament.startDate) {
      items.push({
        title: 'Tournament kickoff',
        date: formatDate(safeTournament.startDate),
        time: formatTime(safeTournament.startDate),
        status: safeTournament.status === 'Upcoming' ? 'upcoming' : 'completed'
      });
    }
    if (safeTournament.endDate) {
      items.push({
        title: 'Final day',
        date: formatDate(safeTournament.endDate),
        time: formatTime(safeTournament.endDate),
        status: safeTournament.status === 'Completed' ? 'completed' : 'upcoming'
      });
    }
    return items;
  }, [safeTournament]);

  const recentRegistrations = useMemo(
    () =>
      players
        .slice(-6)
        .reverse()
        .map((player) => ({
          id: player._id,
          name: player.name,
          role: player.role || 'Player',
          city: player.city || '—',
          timestamp: formatDate(player.registeredAt, 'Recently'),
          avatar: player.photo ? (() => {
            const photo = player.photo;
            if (!photo) return null;
            if (photo.startsWith('http')) return photo;
            if (photo.startsWith('blob:')) return photo;
            if (photo.startsWith('data:')) return photo;
            let path = photo;
            if (path.startsWith('/')) path = path.slice(1);
            if (path.startsWith('uploads')) return `${API_BASE_URL}/${path}`;
            return `${API_BASE_URL}/uploads/${path}`;
          })() : null
        })),
    [players]
  );

  const quickActions = useMemo(
    () => [
      {
        icon: '👥',
        title: 'Manage Players',
        description: 'View, approve & edit player registrations',
        handler: () => navigate(`/tournament/${code}/players`),
        featureId: 'registration_portal'
      },
      {
        icon: '🏆',
        title: 'Manage Teams',
        description: 'Review squads and confirm captains',
        handler: () => navigate(`/tournament/${code}/teams`),
        featureId: 'tournament_core'
      },
      {
        icon: '📅',
        title: 'Schedule',
        description: 'View fixtures and match timings',
        handler: () => navigate(`/tournament/${code}/schedule`),
        featureId: 'tournament_core'
      },
      {
        icon: '📊',
        title: 'Reports',
        description: 'Analytics and tournament insights',
        handler: () => navigate(`/tournament/${code}/report`),
        featureId: 'tournament_core'
      },
      {
        icon: '🔗',
        title: 'Share Links',
        description: 'Copy and broadcast registration URLs',
        handler: () => navigate(`/tournament/${code}/links`),
        featureId: 'registration_portal'
      },
      {
        icon: '🎤',
        title: 'Launch Auction',
        description: 'Enter the live auction console',
        handler: startAuction,
        featureId: 'auction_live'
      }
    ],
    [code, navigate, startAuction]
  );

  const handleQuickAction = (action) => {
    if (action.featureId) {
      if (featuresLoading) {
        toast.info('Checking feature permissions…');
        return;
      }
      if (!hasFeature(action.featureId)) {
        toast.info('🔒 This feature is locked for this plan.');
        return;
      }
    }
    action.handler();
  };

  const getLogoUrl = () => {
    if (!tournament?.logo) return null;
    if (tournament.logo.startsWith('http')) return tournament.logo;
    if (tournament.logo.startsWith('blob:')) return tournament.logo;
    if (tournament.logo.startsWith('data:')) return tournament.logo;
    
    // For relative paths, prepend API_BASE_URL
    let path = tournament.logo;
    if (path.startsWith('/')) {
      path = path.slice(1); // Remove leading slash
    }
    if (path.startsWith('uploads')) {
      return `${API_BASE_URL}/${path}`;
    }
    return `${API_BASE_URL}/${path}`;
  };

  const mapStatusTone = (value) => {
    const normalized = (value || '').toLowerCase();
    if (normalized.includes('active')) return 'status-chip--active';
    if (normalized.includes('complete') || normalized.includes('end')) return 'status-chip--completed';
    if (normalized.includes('close')) return 'status-chip--closed';
    return 'status-chip--upcoming';
  };

  const getRegistrationActionLabel = (status) => {
    if (status === 'Active') return 'Close Registration';
    if (status === 'Not Started') return 'Start Registration';
    return 'Reopen Registration';
  };

  const playerProgressPct = Math.min((registeredPlayers / totalPlayerPoolSize) * 100, 100);
  const teamProgressPct = Math.min((registeredTeams / maxTeams) * 100, 100);
  const playerRegistrationLink = `${baseUrl}/register/${code}`;
  const teamRegistrationLink = `${baseUrl}/register/team/${code}`;

  // Loading state
  if (loading) {
    return (
      <TournamentAdminLayout>
        <div className="overview-pro">
          <div className="ovr-loading">
            <div className="ovr-loading__spinner" />
            <p className="ovr-loading__text">Loading tournament dashboard...</p>
          </div>
        </div>
      </TournamentAdminLayout>
    );
  }

  // Error state
  if (!tournament) {
    return (
      <TournamentAdminLayout>
        <div className="overview-pro">
          <div className="ovr-empty">
            <div className="ovr-empty__icon">⚠️</div>
            <p className="ovr-empty__text">Unable to load tournament details. Please refresh or check your connection.</p>
          </div>
        </div>
      </TournamentAdminLayout>
    );
  }

  const statusTone = (tournament.status || 'Upcoming').toLowerCase();

  return (
    <TournamentAdminLayout>
      <div className="overview-pro ovr-page">
        {/* Light top bar: title + meta + stats + actions */}
        <header className="ovr-top">
          <div className="ovr-top__brand">
            <div className={`ovr-top__logo ${!tournament.logo ? 'fallback' : ''}`}>
              {tournament.logo ? (
                <img src={getLogoUrl()} alt={`${tournament.name} logo`} />
              ) : (
                '🏆'
              )}
            </div>
            <div className="ovr-top__meta">
              <h1 className="ovr-top__title">{tournament.name}</h1>
              <p className="ovr-top__sub">
                <span className="ovr-top__code">{tournament.code}</span>
                {tournament.location && <span> · {tournament.location}</span>}
                {tournament.startDate && (
                  <span> · {formatDate(tournament.startDate)} – {formatDate(tournament.endDate)}</span>
                )}
                {tournament.sport && <span className="mobile-hide"> · {tournament.sport}</span>}
              </p>
              <div className="ovr-top__badges ovr-top__badges--hide-sm">
                <span className={`ovr-top__badge ovr-top__badge--status ${statusTone}`}>
                  {tournament.status || 'Upcoming'}
                </span>
                <span className="ovr-top__badge">{planLabel} Plan</span>
              </div>
            </div>
          </div>
          <div className="ovr-top__stats">
            {heroStats.map((stat) => (
              <div
                key={stat.label}
                className={`ovr-top__stat${stat.hideOnMobile ? ' ovr-top__stat--hide-sm' : ''}`}
              >
                <span className="ovr-top__stat-value">{stat.value}</span>
                <span className="ovr-top__stat-label">{stat.label}</span>
                <span className="ovr-top__stat-hint">{stat.hint}</span>
              </div>
            ))}
          </div>
          <div className="ovr-top__actions">
            {isSuperAdmin && (
              <button type="button" className="ovr-top__btn ovr-top__btn--ghost mobile-hide" onClick={handleBackNavigation}>
                ← Back
              </button>
            )}
            <button
              type="button"
              className="ovr-top__btn ovr-top__btn--primary"
              onClick={() => navigate(`/tournament/${code}/players`)}
              disabled={!canManagePlayers}
            >
              Players
            </button>
            <button
              type="button"
              className="ovr-top__btn ovr-top__btn--outline"
              onClick={() => navigate(`/tournament/${code}/teams`)}
            >
              Teams
            </button>
            <button
              type="button"
              className="ovr-top__btn ovr-top__btn--outline mobile-hide"
              onClick={startAuction}
              disabled={!canStartAuction}
            >
              Auction
            </button>
          </div>
        </header>

        {/* Quick actions: horizontal strip */}
        <nav className="ovr-quick-strip">
          <div className="ovr-quick-strip__list">
            {quickActions.map((action) => {
              const locked = action.featureId ? (!featuresLoading && !hasFeature(action.featureId)) : false;
              const actionClass = action.title.toLowerCase().replace(/\s+/g, '-');
              return (
                <button
                  key={action.title}
                  type="button"
                  className={`ovr-quick-strip__item ${locked ? 'is-locked' : ''} quick-action-${actionClass}`}
                  onClick={() => handleQuickAction(action)}
                  disabled={locked}
                  title={action.description}
                >
                  <span className="ovr-quick-strip__icon">{action.icon}</span>
                  <span className="ovr-quick-strip__label">{action.title}</span>
                </button>
              );
            })}
          </div>
          <div className="ovr-quick-strip__utils">
            <button
              type="button"
              className="ovr-quick-strip__util"
              onClick={() => window.open(`/tournament/${code}/report`, '_blank', 'noopener,noreferrer')}
            >
              📄 Report
            </button>
            <button
              type="button"
              className="ovr-quick-strip__util"
              onClick={() => fetchData(true)}
              disabled={refreshing}
            >
              {refreshing ? '⏳' : '🔄'} Refresh
            </button>
          </div>
        </nav>

        {/* Single card: Registration (two columns) */}
        <section className="ovr-card ovr-registration-card">
          <h2 className="ovr-card__title">Registration</h2>
          <div className="ovr-registration-card__cols">
            {/* Player column */}
            <div className="ovr-registration-card__col ovr-registration-card__col--player">
              <div className="ovr-registration-card__col-head">
                <h3 className="ovr-registration-card__col-title">Players</h3>
                <span className={`ovr-registration-card__status ${mapStatusTone(playerStatus)}`}>{playerStatus}</span>
              </div>
              {featuresLoading ? (
                <div className="ovr-registration-card__col-body">
                  <p className="ovr-loading__text">Checking permissions...</p>
                </div>
              ) : !hasFeature('registration_portal') ? (
                <div className="ovr-registration-card__col-body">
                  <div className="ovr-feature-locked">
                    <div className="ovr-feature-locked__icon">🔒</div>
                    <p className="ovr-feature-locked__text">Registration locked</p>
                    <span className="ovr-feature-locked__plan">{planLabel}</span>
                  </div>
                </div>
              ) : (
                <div className="ovr-registration-card__col-body">
                  <div className="ovr-registration-card__count">
                    <span className="ovr-registration-card__count-num">{registeredPlayers}</span>
                    <span className="ovr-registration-card__count-of">/ {totalPlayerPoolSize}</span>
                  </div>
                  <div className="ovr-progress">
                    <div className="ovr-progress__bar" style={{ width: `${playerProgressPct}%` }} />
                  </div>
                  <div className="ovr-link-row">
                    <input type="text" value={playerRegistrationLink} readOnly className="ovr-link-row__input" />
                    <button type="button" className="ovr-link-row__btn" onClick={() => copyLink('player')}>Copy</button>
                    <button type="button" className="ovr-link-row__btn secondary" onClick={() => shareOnWhatsApp('player')}>Share</button>
                  </div>
                  <div className="ovr-registration-card__actions">
                    <button type="button" className="ovr-registration-card__btn primary" onClick={() => setModalState({ open: true, scope: 'player' })}>
                      {getRegistrationActionLabel(playerStatus)}
                    </button>
                    <button type="button" className="ovr-registration-card__btn ghost" onClick={() => navigate(`/tournament/${code}/players`)}>Roster →</button>
                  </div>
                </div>
              )}
            </div>
            {/* Team column */}
            <div className="ovr-registration-card__col ovr-registration-card__col--team">
              <div className="ovr-registration-card__col-head">
                <h3 className="ovr-registration-card__col-title">Teams</h3>
                <span className={`ovr-registration-card__status ${mapStatusTone(teamStatus)}`}>{teamStatus}</span>
              </div>
              {featuresLoading ? (
                <div className="ovr-registration-card__col-body">
                  <p className="ovr-loading__text">Checking permissions...</p>
                </div>
              ) : !hasFeature('registration_portal') ? (
                <div className="ovr-registration-card__col-body">
                  <div className="ovr-feature-locked">
                    <div className="ovr-feature-locked__icon">🔒</div>
                    <p className="ovr-feature-locked__text">Registration locked</p>
                    <span className="ovr-feature-locked__plan">{planLabel}</span>
                  </div>
                </div>
              ) : (
                <div className="ovr-registration-card__col-body">
                  <div className="ovr-registration-card__count">
                    <span className="ovr-registration-card__count-num">{registeredTeams}</span>
                    <span className="ovr-registration-card__count-of">/ {maxTeams}</span>
                  </div>
                  <div className="ovr-progress">
                    <div className="ovr-progress__bar" style={{ width: `${teamProgressPct}%` }} />
                  </div>
                  <div className="ovr-link-row">
                    <input type="text" value={teamRegistrationLink} readOnly className="ovr-link-row__input" />
                    <button type="button" className="ovr-link-row__btn" onClick={() => copyLink('team')}>Copy</button>
                    <button type="button" className="ovr-link-row__btn secondary" onClick={() => shareOnWhatsApp('team')}>Share</button>
                  </div>
                  <div className="ovr-registration-card__actions">
                    <button type="button" className="ovr-registration-card__btn primary" onClick={() => setModalState({ open: true, scope: 'team' })}>
                      {getRegistrationActionLabel(teamStatus)}
                    </button>
                    <button type="button" className="ovr-registration-card__btn ghost" onClick={() => navigate(`/tournament/${code}/teams`)}>Review →</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Single card: Schedule + Recent activity (two columns) */}
        <section className="ovr-card ovr-schedule-activity">
          <div className="ovr-schedule-activity__timeline">
            <h3 className="ovr-schedule-activity__heading">Schedule</h3>
            {timelineItems.length ? (
              <ul className="ovr-timeline">
                {timelineItems.map((item) => (
                  <li key={`${item.title}-${item.date}`} className={`ovr-timeline__item ${item.status}`}>
                    <div className="ovr-timeline__dot" />
                    <div className="ovr-timeline__content">
                      <strong>{item.title}</strong>
                      <span>{item.date}{item.time ? ` · ${item.time}` : ''}</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="ovr-empty">
                <div className="ovr-empty__icon">📅</div>
                <p className="ovr-empty__text">Add tournament dates to see milestones.</p>
              </div>
            )}
          </div>
          <div className="ovr-schedule-activity__recent">
            <div className="ovr-schedule-activity__heading-row">
              <h3 className="ovr-schedule-activity__heading">Recent registrations</h3>
              <button type="button" className="ovr-schedule-activity__link" onClick={() => navigate(`/tournament/${code}/players`)}>View all →</button>
            </div>
            {recentRegistrations.length ? (
              <div className="ovr-activity">
                {recentRegistrations.map((entry) => (
                  <div key={entry.id} className="ovr-activity__item">
                    <div className="ovr-activity__avatar">
                      {entry.avatar ? <img src={entry.avatar} alt={entry.name} /> : entry.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="ovr-activity__info">
                      <p className="ovr-activity__name">{entry.name}</p>
                      <p className="ovr-activity__meta">{entry.role} · {entry.city}</p>
                    </div>
                    <span className="ovr-activity__time">{entry.timestamp}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ovr-empty">
                <div className="ovr-empty__icon">👥</div>
                <p className="ovr-empty__text">No registrations yet. Share your links to get started.</p>
              </div>
            )}
          </div>
        </section>

        {/* Auction summary bar */}
        <section className="ovr-card ovr-auction-bar">
          <div className="ovr-auction-bar__head">
            <h3 className="ovr-auction-bar__title">{auctionData ? 'Auction' : 'Auction not started'}</h3>
            <button
              type="button"
              className="ovr-auction-bar__cta"
              onClick={startAuction}
              disabled={featuresLoading || !hasFeature('auction_live')}
            >
              Enter auction
            </button>
          </div>
          {featuresLoading ? (
            <p className="ovr-loading__text">Checking permissions...</p>
          ) : hasFeature('auction_live') ? (
            <div className="ovr-auction-bar__stats">
              <div className="ovr-auction-bar__stat">
                <span className="ovr-auction-bar__stat-value">{soldPlayers}</span>
                <span className="ovr-auction-bar__stat-label">Sold</span>
              </div>
              <div className="ovr-auction-bar__stat">
                <span className="ovr-auction-bar__stat-value">₹{totalSpend.toLocaleString('en-IN')}</span>
                <span className="ovr-auction-bar__stat-label">Total</span>
              </div>
              <div className="ovr-auction-bar__stat">
                <span className="ovr-auction-bar__stat-value">{highestBid ? `₹${highestBid.toLocaleString('en-IN')}` : '—'}</span>
                <span className="ovr-auction-bar__stat-label">Highest</span>
              </div>
              <div className="ovr-auction-bar__stat">
                <span className="ovr-auction-bar__stat-value">{soldPlayers ? `₹${Math.round(totalSpend / soldPlayers).toLocaleString('en-IN')}` : '—'}</span>
                <span className="ovr-auction-bar__stat-label">Avg</span>
              </div>
            </div>
          ) : (
            <div className="ovr-feature-locked">
              <div className="ovr-feature-locked__icon">🔒</div>
              <p className="ovr-feature-locked__text">Live auction not available in your plan</p>
              <span className="ovr-feature-locked__plan">{planLabel}</span>
            </div>
          )}
        </section>

        {/* Registration Modal */}
        {modalState.open && (
          <div className="ovr-modal" role="dialog" aria-modal="true">
            <div className="ovr-modal__dialog">
              <header className="ovr-modal__header">
                <h3 className="ovr-modal__title">Confirm Registration Update</h3>
                <button
                  type="button"
                  className="ovr-modal__close"
                  onClick={() => setModalState({ open: false, scope: 'player' })}
                >
                  ×
                </button>
              </header>
              <div className="ovr-modal__body">
                <p className="ovr-modal__text">
                  {(() => {
                    const status = computeRegistrationStatus(modalState.scope);
                    if (status === 'Active') return `Are you sure you want to close ${modalState.scope} registration? New registrations will be blocked.`;
                    if (status === 'Not Started') return `Ready to open ${modalState.scope} registration? Players will be able to register through your link.`;
                    return `Reopen ${modalState.scope} registration? This will allow new registrations again.`;
                  })()}
                </p>
              </div>
              <footer className="ovr-modal__footer">
                <button
                  type="button"
                  className="ovr-modal__btn ovr-modal__btn--cancel"
                  onClick={() => setModalState({ open: false, scope: 'player' })}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ovr-modal__btn ovr-modal__btn--confirm"
                  onClick={() =>
                    mutateRegistration(
                      computeRegistrationStatus(modalState.scope) === 'Active' ? 'close' : 'reopen',
                      modalState.scope
                    )
                  }
                >
                  Confirm
                </button>
              </footer>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="ovr-footer">
        <img src="/logo192.png" alt="PlayLive Pro" className="ovr-footer__logo" />
        <p className="ovr-footer__text">Powered by <strong>PlayLive Pro</strong> — Tournament Made Simple</p>
      </footer>
    </TournamentAdminLayout>
  );
}

export default TournamentOverview;
